import { DefaultCustomField } from 'src/constants/types';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactRenderer } from '@tiptap/react'
import tippy from 'tippy.js'
import { CustomFields } from './CustomFields'
import { PluginKey } from '@tiptap/pm/state';
import { isDateType, isDateTimeType } from 'src/helpers/formatCustomField';

export function configureCustomFieldSuggestions(
  customFieldsRef: { current: DefaultCustomField[] },
) {
  return {
    pluginKey: new PluginKey("suggestions"),
    items: ({ query }: {query: string}) => {
      const customFields = customFieldsRef.current;
      if (!customFields || customFields.length === 0) {
        return [];
      }
      
      const baseItems = customFields
        .filter(field => {
          const isCompanyField = field.name.startsWith('company.standard.') || field.name.startsWith('company.custom.');
          if (isCompanyField && !field.hasDefaultValue) {
            return false;
          }
          return true;
        })
        .map(m => ({name: m.name, description: m.description, type: m.type}))
        .filter(item =>
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
        );
      
      // Add formatting options for date and datetime fields
      const itemsWithFormats: Array<{name: string, description: string, type?: string | null}> = [];
      
      baseItems.forEach(item => {
        itemsWithFormats.push(item);

        if (isDateTimeType(item.type)) {
          itemsWithFormats.push({
            name: item.name + '.date',
            description: `${item.description} (date only)`,
            type: item.type
          });
          itemsWithFormats.push({
            name: item.name + '.time',
            description: `${item.description} (time only)`,
            type: item.type
          });
        }

        if (isDateType(item.type)) {
          itemsWithFormats.push({
            name: item.name + '.DMY',
            description: `${item.description} (dd/mm/yyyy)`,
            type: item.type
          });
          itemsWithFormats.push({
            name: item.name + '.YMD',
            description: `${item.description} (yyyy/mm/dd)`,
            type: item.type
          });
          itemsWithFormats.push({
            name: item.name + '.MDY',
            description: `${item.description} (mm/dd/yyyy)`,
            type: item.type
          });
        }
      });

      return itemsWithFormats;
    },
    char: '{',
    command: ({ editor, range, props }: {editor: any, range: any, props: any}) => {
      // increase range.to by one when the next node is of type "text"
      // and starts with a space character
      const nodeAfter = editor.view.state.selection.$to.nodeAfter
      const overrideSpace = nodeAfter?.text
        ? /^\s/.test(nodeAfter.text)
        : false

      if (overrideSpace) {
        range.to += 1
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'customFieldMention',
            attrs: props,
          },
        ])
        .run()
    },
    allowPrefixes: null,
    render: () => {
      let reactRenderer: any;
      let popup: any;
  
      return {
        onStart: (props: { clientRect?: any; editor: any; items?: any[] }) => {
  
          if (!props.clientRect) {
            return
          }

          if (!props.items || props.items.length === 0) {
            return
          }
  
          reactRenderer = new ReactRenderer(CustomFields, {
            props,
            editor: props.editor,
          })
  
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: reactRenderer.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          })
        },
  
        onUpdate(props: { clientRect?: any; items?: any[] }) {
          if (!props.items || props.items.length === 0) {
            if (popup && popup[0]) {
              popup[0].hide()
            }
            return
          }

          if (reactRenderer) {
            reactRenderer.updateProps(props)
          }
  
          if (!props.clientRect) {
            return
          }
  
          if (popup && popup[0]) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect,
            })
            popup[0].show()
          }
        },
  
        onKeyDown(props: { event: { key: string } }) {
          if (!reactRenderer) {
            return false
          }

          if (props.event.key === 'Escape') {
            popup?.[0]?.hide()
  
            return true
          }
  
          return reactRenderer.ref?.onKeyDown(props)
        },
  
        onExit() {
          popup?.[0]?.destroy()
          reactRenderer?.destroy()
        },
      }
    }
  }
}
